import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import Settings, { type SettingsOpenOptions } from './components/Settings';
import Sidebar from './components/Sidebar';
import { FlowsView } from './components/flows/FlowsView';
import { ToastContainer, type ToastItem } from './components/Toast';
import { CoworkView } from './components/cowork';
import { ScheduledTasksView } from './components/scheduledTasks';
import { LogManagerView } from './components/logManager';
import { ErrorBoundary } from './components/ErrorBoundary';
import DashboardView from './components/dashboard/DashboardView';
import MobileDashboard from './components/dashboard/MobileDashboard';
import DetectView from './components/detect/DetectView';
import SequenceDetectView from './components/sequenceDetect/SequenceDetectView';
import ExternalAppView from './components/external/ExternalAppView';
import { getExternalApp } from './services/externalApps';
import type { ExternalAppId } from './services/externalApps';
import { configService } from './services/config';
import { apiService } from './services/api';
import { themeService } from './services/theme';
import { coworkService } from './services/cowork';
import { defaultConfig } from './config';
import { setAvailableModels, setSelectedModel } from './store/slices/modelSlice';
import { setCurrentSessionId, clearCurrentSession } from './store/slices/coworkSlice';
import { setViewMode, setSelectedPet, setSelectedIssueType, setSelectedTimestamp, setVisiblePanels, type DashboardPanelId } from './store/slices/dashboardSlice';
import type { ApiConfig } from './services/api';

import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { i18nService } from './services/i18n';
import { matchesShortcut } from './services/shortcuts';

declare global {
  interface Window {
    __APP_MODE__?: 'dev' | 'demo';
  }
}

const DemoWarningBanner: React.FC = () => {
  if (window.__APP_MODE__ !== 'demo') return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 px-3 py-2 mb-1 rounded-lg border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/60 text-amber-900 dark:text-amber-200 text-xs leading-relaxed"
    >
      <span className="flex-shrink-0 text-base leading-none mt-0.5">⚠️</span>
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-amber-800 dark:text-amber-300">Demo Version</span>
        <span className="ml-1.5">— Do not enter sensitive personal, health, or medical data. This is a public demo and your data is not protected.</span>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [settingsOptions, setSettingsOptions] = useState<SettingsOpenOptions>({});
  const [mainView, setMainView] = useState<'cowork' | 'flows' | 'scheduledTasks' | 'logManager' | 'dashboard' | 'mobileDashboard' | 'sequenceDetect' | 'detect' | ExternalAppId>('cowork');
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [, forceLanguageRefresh] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const hasInitialized = useRef(false);
  const dispatch = useDispatch();
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const sessions = useSelector((state: RootState) => state.cowork.sessions);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeApp = async () => {
      try {
        await configService.init();
        themeService.initialize();
        await i18nService.initialize();

        const config = await configService.getConfig();
        const apiConfig: ApiConfig = {
          apiKey: config.api.key,
          baseUrl: config.api.baseUrl,
        };
        apiService.setConfig(apiConfig);

        const providerModels: { id: string; name: string; provider?: string; providerKey?: string; supportsImage?: boolean }[] = [];
        if (config.providers) {
          Object.entries(config.providers).forEach(([providerName, providerConfig]) => {
            if (providerConfig.enabled && providerConfig.models) {
              providerConfig.models.forEach((model: { id: string; name: string; supportsImage?: boolean }) => {
                providerModels.push({
                  id: model.id,
                  name: model.name,
                  provider: providerName.charAt(0).toUpperCase() + providerName.slice(1),
                  providerKey: providerName,
                  supportsImage: model.supportsImage ?? false,
                });
              });
            }
          });
        }
        const fallbackModels = config.model.availableModels.map(model => ({
          id: model.id,
          name: model.name,
          providerKey: undefined,
          supportsImage: model.supportsImage ?? false,
        }));
        const resolvedModels = providerModels.length > 0 ? providerModels : fallbackModels;
        if (resolvedModels.length > 0) {
          dispatch(setAvailableModels(resolvedModels));
          const preferredModel = resolvedModels.find(
            model => model.id === config.model.defaultModel
              && (!config.model.defaultModelProvider || model.providerKey === config.model.defaultModelProvider)
          ) ?? resolvedModels[0];
          dispatch(setSelectedModel(preferredModel));
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setInitError(i18nService.t('initializationError'));
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    const unsubscribe = i18nService.subscribe(() => {
      forceLanguageRefresh((prev) => prev + 1);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleNavigateToPlayback = () => setMainView('dashboard');
    window.addEventListener('navigate-to-playback', handleNavigateToPlayback);
    return () => window.removeEventListener('navigate-to-playback', handleNavigateToPlayback);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode !== 'playback') return;

    const pet = params.get('pet') || '';
    const type = params.get('type') || '';
    const timestamp = params.get('timestamp') || '';
    const panelsParam = params.get('panels');
    const VALID_PANELS: DashboardPanelId[] = ['heartRate', 'breathRate', 'thermal', 'petDetection', 'images'];
    const panels: DashboardPanelId[] = panelsParam
      ? panelsParam.split(',').filter((p): p is DashboardPanelId => VALID_PANELS.includes(p as DashboardPanelId))
      : ['heartRate', 'breathRate', 'thermal', 'petDetection'];

    dispatch(setViewMode('playback'));
    if (pet) dispatch(setSelectedPet(pet));
    if (type) dispatch(setSelectedIssueType(type));
    if (timestamp) dispatch(setSelectedTimestamp(timestamp));
    if (panels.length > 0) dispatch(setVisiblePanels(panels));
    setMainView('dashboard');
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialized || !selectedModel?.id) return;
    const config = configService.getConfig();
    if (config.model.defaultModel === selectedModel.id && (config.model.defaultModelProvider ?? '') === (selectedModel.providerKey ?? '')) return;
    void configService.updateConfig({
      model: { ...config.model, defaultModel: selectedModel.id, defaultModelProvider: selectedModel.providerKey },
    });
  }, [isInitialized, selectedModel?.id, selectedModel?.providerKey]);

  const handleShowSettings = useCallback((options?: SettingsOpenOptions) => {
    setSettingsOptions({ initialTab: options?.initialTab, notice: options?.notice });
    setShowSettings(true);
  }, []);

  const handleShowCowork = useCallback(() => setMainView('cowork'), []);
  const handleShowFlows = useCallback(() => setMainView('flows'), []);
  const handleShowScheduledTasks = useCallback(() => setMainView('scheduledTasks'), []);
  const handleShowLogManager = useCallback(() => setMainView('logManager'), []);
  const handleShowDashboard = useCallback(() => setMainView('dashboard'), []);
  const handleShowMobileDashboard = useCallback(() => setMainView('mobileDashboard'), []);
  const handleShowSequenceDetect = useCallback(() => setMainView('sequenceDetect'), []);
  const handleShowDetect = useCallback(() => setMainView('detect'), []);
  const handleShowExternalApp = useCallback((id: string) => setMainView(id as ExternalAppId), []);
  const handleToggleSidebar = useCallback(() => setIsSidebarCollapsed((prev) => !prev), []);

  const handleNewChat = useCallback(async () => {
    dispatch(clearCurrentSession());
    setMainView('cowork');
  }, [dispatch]);

  const handleStartLogAssistant = useCallback(async () => {
    const logAssistantSession = sessions.find(s => s.activeSkillIds?.includes('log-assistant'));
    if (logAssistantSession) {
      dispatch(setCurrentSessionId(logAssistantSession.id));
      await coworkService.loadSession(logAssistantSession.id);
      setMainView('cowork');
    } else {
      setMainView('cowork');
      const systemPrompt = `You are a **Log Assistant** agent specialized in helping users record and manage log entries.`;
      const session = await coworkService.startSession({ prompt: 'Start Log Assistant', systemPrompt, activeSkillIds: ['log-assistant'], title: 'Log Assistant' });
      if (session) dispatch(setCurrentSessionId(session.id));
    }
  }, [dispatch, sessions]);

  const showToast = useCallback((message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleShowLogin = useCallback(() => {
    showToast(i18nService.t('featureInDevelopment'));
  }, [showToast]);

  const handleCloseSettings = () => {
    setShowSettings(false);
    const config = configService.getConfig();
    apiService.setConfig({ apiKey: config.api.key, baseUrl: config.api.baseUrl });
    if (config.providers) {
      const allModels: { id: string; name: string; provider?: string; providerKey?: string; supportsImage?: boolean }[] = [];
      Object.entries(config.providers).forEach(([providerName, providerConfig]) => {
        if (providerConfig.enabled && providerConfig.models) {
          providerConfig.models.forEach((model: { id: string; name: string; supportsImage?: boolean }) => {
            allModels.push({ id: model.id, name: model.name, provider: providerName.charAt(0).toUpperCase() + providerName.slice(1), providerKey: providerName, supportsImage: model.supportsImage ?? false });
          });
        }
      });
      if (allModels.length > 0) dispatch(setAvailableModels(allModels));
    }
  };

  const isShortcutInputActive = () => {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return false;
    return activeElement.dataset.shortcutInput === 'true';
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || isShortcutInputActive()) return;
      const { shortcuts } = configService.getConfig();
      const activeShortcuts = { ...defaultConfig.shortcuts, ...(shortcuts ?? {}) };
      if (matchesShortcut(event, activeShortcuts.newChat)) { event.preventDefault(); handleNewChat(); return; }
      if (matchesShortcut(event, activeShortcuts.search)) { event.preventDefault(); window.dispatchEvent(new CustomEvent('cowork:shortcut:search')); return; }
      if (matchesShortcut(event, activeShortcuts.settings)) { event.preventDefault(); handleShowSettings(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleShowSettings, handleNewChat]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail === 'string') showToast(detail);
      else if (detail && typeof detail === 'object') showToast(detail.message || String(detail), detail.type);
    };
    window.addEventListener('app:showToast', handler);
    return () => window.removeEventListener('app:showToast', handler);
  }, [showToast]);

  useEffect(() => {
    const handleViewSession = async (event: Event) => {
      const { sessionId } = (event as CustomEvent).detail;
      if (sessionId) { setMainView('cowork'); await coworkService.loadSession(sessionId); }
    };
    window.addEventListener('scheduledTask:viewSession', handleViewSession);
    return () => window.removeEventListener('scheduledTask:viewSession', handleViewSession);
  }, []);

  if (!isInitialized) {
    return (
      <div className="h-screen overflow-hidden flex flex-col">
        <div className="flex-1 flex items-center justify-center dark:bg-claude-darkBg bg-claude-bg">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-claude-accent to-claude-accentHover flex items-center justify-center shadow-glow-accent animate-pulse">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
            <div className="w-24 h-1 rounded-full bg-claude-accent/20 overflow-hidden">
              <div className="h-full w-1/2 rounded-full bg-claude-accent animate-shimmer" />
            </div>
            <div className="dark:text-claude-darkText text-claude-text text-xl font-medium">{i18nService.t('loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="h-screen overflow-hidden flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center dark:bg-claude-darkBg bg-claude-bg">
          <div className="flex flex-col items-center space-y-6 max-w-md px-6">
            <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
            <div className="dark:text-claude-darkText text-claude-text text-xl font-medium text-center">{initError}</div>
            <button onClick={() => handleShowSettings()} className="px-6 py-2.5 bg-claude-accent hover:bg-claude-accentHover text-white rounded-xl shadow-md transition-colors text-sm font-medium">
              {i18nService.t('openSettings')}
            </button>
          </div>
          {showSettings && <Settings onClose={handleCloseSettings} initialTab={settingsOptions.initialTab} notice={settingsOptions.notice} />}
        </div>
      </div>
    );
  }

  const externalApp = getExternalApp(mainView);

  return (
    <div className="h-screen overflow-hidden flex flex-col dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted">
      <DemoWarningBanner />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <Sidebar
          onShowLogin={handleShowLogin}
          onShowSettings={handleShowSettings}
          activeView={mainView}
          onShowFlows={handleShowFlows}
          onShowCowork={handleShowCowork}
          onShowScheduledTasks={handleShowScheduledTasks}
          onShowLogManager={handleShowLogManager}
          onShowDashboard={handleShowDashboard}
          onShowMobileDashboard={handleShowMobileDashboard}
          onShowSequenceDetect={handleShowSequenceDetect}
          onShowDetect={handleShowDetect}
          onShowExternalApp={handleShowExternalApp}
          onNewChat={handleNewChat}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
        />
        <div className={`flex-1 min-w-0 min-h-0 py-1.5 pr-1.5 ${isSidebarCollapsed ? 'pl-1.5' : ''}`}>
          <div className="h-full min-h-0 rounded-xl dark:bg-claude-darkBg bg-claude-bg overflow-hidden">
            <ErrorBoundary>
            {mainView === 'scheduledTasks' && window.__APP_MODE__ !== 'demo' ? (
              <ScheduledTasksView isSidebarCollapsed={isSidebarCollapsed} onToggleSidebar={handleToggleSidebar} onNewChat={handleNewChat} />
            ) : mainView === 'logManager' ? (
              <LogManagerView onStartLogAssistant={handleStartLogAssistant} onNavigateHome={handleShowCowork} />
            ) : mainView === 'dashboard' ? (
              <DashboardView onNavigateHome={handleShowCowork} />
            ) : mainView === 'mobileDashboard' ? (
              <MobileDashboard onNavigateHome={handleShowCowork} />
            ) : mainView === 'sequenceDetect' ? (
              <SequenceDetectView onNavigateHome={handleShowCowork} />
            ) : mainView === 'detect' ? (
              <DetectView onNavigateHome={handleShowCowork} />
            ) : externalApp ? (
              <ExternalAppView app={externalApp} onNavigateHome={handleShowCowork} />
            ) : mainView === 'flows' && window.__APP_MODE__ !== 'demo' ? (
              <FlowsView onSelectFlow={() => { setMainView('cowork'); }} />
            ) : (
              <CoworkView onRequestAppSettings={handleShowSettings} isSidebarCollapsed={isSidebarCollapsed} onToggleSidebar={handleToggleSidebar} onNewChat={handleNewChat} />
            )}
            </ErrorBoundary>
          </div>
        </div>
      </div>
      {showSettings && <Settings onClose={handleCloseSettings} initialTab={settingsOptions.initialTab} notice={settingsOptions.notice} />}
    </div>
  );
};

export default App;